/**
 * 用户详情抽屉（旧 views/users/UserDetail.vue）：基本信息、学习统计、启用/禁用、重置密码
 */
import { App, Avatar, Badge, Button, Col, Drawer, Row, Space, Spin, Statistic, Typography } from 'antd'
import { CrownOutlined } from '@ant-design/icons'
import { useEffect, useState, type ReactNode } from 'react'
import { formatStudyDuration } from '@/utils/format'
import { useUpdateUserStatus, useUserDetail } from './hooks'
import { ResetPasswordModal } from './ResetPasswordModal'
import styles from './UserDetailDrawer.module.css'

function formatDate(value?: string | null) {
  if (!value) return '-'
  return new Date(value).toLocaleString('zh-CN')
}

function InfoItem({ label, children }: Readonly<{ label: string; children: ReactNode }>) {
  return (
    <div>
      <Typography.Text type="secondary" className={styles.label}>
        {label}
      </Typography.Text>
      <div>{children}</div>
    </div>
  )
}

interface UserDetailDrawerProps {
  // 为 null 时抽屉关闭
  userId: number | null
  onClose: () => void
}

export function UserDetailDrawer({ userId, onClose }: Readonly<UserDetailDrawerProps>) {
  const { message } = App.useApp()
  const { data: user, isLoading, isError } = useUserDetail(userId)
  const updateStatus = useUpdateUserStatus()
  const [resetPasswordOpen, setResetPasswordOpen] = useState(false)

  useEffect(() => {
    if (isError) message.error('获取用户详情失败')
  }, [isError, message])

  const changeStatus = async (isActive: boolean) => {
    if (userId === null) return
    try {
      await updateStatus.mutateAsync({ userId, isActive })
      message.success('用户状态更新成功')
    } catch (error) {
      console.error('更新用户状态失败:', error)
      message.error('更新用户状态失败')
    }
  }

  const stats = user?.learning_stats

  return (
    <Drawer open={userId !== null} size={720} title="用户详情" placement="right" onClose={onClose}>
      <Spin spinning={isLoading}>
        {user && (
          <div>
            <section className={styles.section}>
              <h3 className={styles.sectionTitle}>基本信息</h3>
              <div className={styles.infoGrid}>
                <InfoItem label="用户头像">
                  <Avatar src={user.avatar || undefined} size={64}>
                    {(user.display_name || user.username).charAt(0)}
                  </Avatar>
                </InfoItem>
                <InfoItem label="用户名">{user.username}</InfoItem>
                <InfoItem label="昵称">{user.display_name || '-'}</InfoItem>
                <InfoItem label="邮箱">{user.email}</InfoItem>
                <InfoItem label="账号状态">
                  <Badge status={user.is_active ? 'success' : 'error'} text={user.is_active ? '活跃' : '禁用'} />
                </InfoItem>
                <InfoItem label="会员状态">
                  {user.is_premium ? (
                    <span>
                      <CrownOutlined className={styles.premium} /> 高级会员
                    </span>
                  ) : (
                    '普通用户'
                  )}
                </InfoItem>
                <InfoItem label="注册时间">{formatDate(user.created_at)}</InfoItem>
                <InfoItem label="最后登录">{user.last_login_at ? formatDate(user.last_login_at) : '从未登录'}</InfoItem>
              </div>
            </section>

            {stats && (
              <section className={styles.section}>
                <h3 className={styles.sectionTitle}>学习统计</h3>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Statistic title="学习时长" value={formatStudyDuration(stats.learning_seconds)} />
                  </Col>
                  <Col span={12}>
                    <Statistic title="已学词汇" value={stats.learned_words ?? 0} suffix="个" />
                  </Col>
                  <Col span={12}>
                    <Statistic title="连续天数" value={stats.streak_days ?? 0} suffix="天" />
                  </Col>
                </Row>
              </section>
            )}

            <section className={styles.section}>
              <Space>
                {user.is_active ? (
                  <Button danger onClick={() => changeStatus(false)}>
                    禁用账号
                  </Button>
                ) : (
                  <Button type="primary" onClick={() => changeStatus(true)}>
                    启用账号
                  </Button>
                )}
                <Button onClick={() => setResetPasswordOpen(true)}>重置密码</Button>
                <Button onClick={onClose}>关闭</Button>
              </Space>
            </section>
          </div>
        )}
      </Spin>

      {user && (
        <ResetPasswordModal
          open={resetPasswordOpen}
          userId={user.id}
          username={user.username}
          onClose={() => setResetPasswordOpen(false)}
        />
      )}
    </Drawer>
  )
}
